/**
 * ConsoleV2 — the agent console's information architecture, rebuilt (CRM UI
 * streamline D-7, approved option A).
 *
 * SEVEN TABS BECAME THREE. The old console asked a recruiter to know that a
 * determination lives under "Review queue", an unapproved reply under "Drafts",
 * a blocked one under "Escalations", and that "Board", "Batches", "Tuning" and
 * "Review" are not day-to-day at all. The rebuild answers the two questions
 * people actually arrive with:
 *
 *   Needs you      — everything waiting on a human, one list, oldest first
 *   Conversations  — the board (D-8's triage lanes)
 *   Manage         — batches / tuning / weekly review, the config drawer
 *
 * THIS FILE OWNS NO DATA AND NO ACTIONS. Every list, every handler and every
 * piece of loading state arrives as a prop from AgentConsole. The only thing it
 * owns is the URL: which tab is showing, which Manage panel, which conversation
 * is selected — and the one-time redirect that keeps every old `?tab=` link
 * landing (tabParams.ts).
 *
 * MOUNT ON FIRST VISIT, THEN NEVER UNMOUNT. A panel appears the first time its
 * tab is opened and stays mounted from then on; only `hidden` moves after that.
 * Flipping to Conversations to check a thread and back must not throw away a
 * half-written determination, a Needs-you selection, or an expanded batch — and
 * unmounting a panel that owns a POST invites sending it twice (the LeadRail
 * precedent, F1). Deferring the FIRST mount is safe because nothing outside a
 * panel reads its state; what it buys is not paying for three panels to render
 * on every console open.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { SegmentedTabs, type SegTab } from '../../../components/filters';
import type { DraftRow } from '../../../components/agent/shared';
import type { StudyOption } from '../../../types';
import type { BoardRow, EscalationRow, Metrics } from '../AgentConsole';
import type { BatchRow, BatchTargetRow } from '../BatchesPanel';
import type { QueueEscalation, QueueReview } from '../AgentReviewQueue';
import BoardLanes from './board/BoardLanes';
import ConsoleKpis from './ConsoleKpis';
import ManageTab from './ManageTab';
import NeedsYouTab, { type QueueDetermination } from './NeedsYouTab';
import { buildNeedsYou, needsYouCapped, needsYouCount, type ActiveEscSummary } from './needsYou';
import { isConsoleTab, normalizeConsoleParams, type ConsoleTab, type ManageSub } from './tabParams';

/** The board fetch's server limit — a full page means "and more". */
const BOARD_LIMIT = 200;

export interface ConsoleV2Props {
  metrics: Metrics | null;
  /** Open-status conversations (limit 200). */
  board: BoardRow[];
  /** status=closed (limit 25) — D-8's Recently-closed lane. */
  closedBoard: BoardRow[];
  drafts: DraftRow[];
  suppressed: DraftRow[];
  /** Rows of the CURRENT escStatus view. */
  escalations: EscalationRow[];
  escStatus: 'active' | 'resolved';
  onEscStatusChange: (s: 'active' | 'resolved') => void;
  /** LATCHED from the last status=active fetch — see needsYou.ts. */
  activeEsc: ActiveEscSummary | null;
  queueEscalations: QueueEscalation[];
  queueReviews: QueueReview[];
  /** The queue endpoint's `determinations` — the delivery strip. Optional: the
   *  page fetches them already but does not hold them in state yet. */
  determinations?: QueueDetermination[];
  batches: BatchRow[];
  batchesAgentEnabled: boolean;
  /** Batches drill-through lives at page level (survives tab flips, classic parity). */
  openBatchId: string | null;
  batchDetail: { id: string; targets: BatchTargetRow[] } | null;
  onToggleBatch: (id: string) => void;
  studies: StudyOption[];
  loading: boolean;
  nowTs: number;
  busyId: string | null;
  refreshTick: number;
  params: URLSearchParams;
  setParams: (updater: (p: URLSearchParams) => URLSearchParams, opts?: { replace?: boolean }) => void;
  onApprove: (t: DraftRow, text?: string) => Promise<void>;
  onReject: (t: DraftRow, reason: string) => Promise<void>;
  onConversationAction: (id: string, action: 'pause' | 'resume' | 'takeover' | 'close', body?: { reason?: string }) => Promise<void>;
  onEscalationAction: (id: string, action: 'claim' | 'resolve') => Promise<void>;
  onCancelBatch: (id: string) => Promise<void>;
  onError: (msg: string) => void;
  /** Board filter state — the SAME setters classic uses (server params). */
  flagsOnly: boolean;
  setFlagsOnly: (v: boolean) => void;
  recentReplyOnly: boolean;
  setRecentReplyOnly: (v: boolean) => void;
  engagedOnly: boolean;
  setEngagedOnly: (v: boolean) => void;
  modeFilter: '' | 'copilot' | 'auto';
  setModeFilter: (v: '' | 'copilot' | 'auto') => void;
  studyFilter: string;
  setStudyFilter: (v: string) => void;
  onRefresh: () => void;
}

export default function ConsoleV2({
  metrics, board, closedBoard, drafts, suppressed, escalations, escStatus, onEscStatusChange,
  activeEsc, queueEscalations, queueReviews, determinations = [], batches, batchesAgentEnabled,
  openBatchId, batchDetail, onToggleBatch, studies, loading, nowTs, busyId, refreshTick,
  params, setParams, onApprove, onReject, onConversationAction, onEscalationAction,
  onCancelBatch, onError, flagsOnly, setFlagsOnly, recentReplyOnly, setRecentReplyOnly,
  engagedOnly, setEngagedOnly, modeFilter, setModeFilter, studyFilter, setStudyFilter, onRefresh,
}: ConsoleV2Props) {
  // ---- URL: which tab, which Manage panel, which conversation ----------
  const { tab, sub, changed, patch } = normalizeConsoleParams(params);
  const patchQuery = patch.toString();
  // Old links (`?tab=queue&review=…` from Today, `?tab=review` from the weekly
  // email, `?tab=board&view=grid` from a bookmark) are forwarded ONCE, in place.
  useEffect(() => {
    if (!changed) return;
    setParams(() => new URLSearchParams(patchQuery), { replace: true });
  }, [changed, patchQuery, setParams]);

  const selectedConvoId = params.get('conversation');
  const reviewParam = params.get('review');

  /** Which panels have ever been shown. A panel mounts on its FIRST visit and
   *  then stays mounted — hidden, never unmounted, so a half-written
   *  determination survives a trip to Conversations and back. Mounting all
   *  three on arrival meant every console open paid for three panels to render
   *  one. Written during render (not in an effect) so the tab being switched TO
   *  is mounted in the commit that reveals it. */
  const visitedRef = useRef<Set<ConsoleTab>>(new Set());
  visitedRef.current.add(tab);
  const visited = visitedRef.current;

  /** The open conversation workspace refetches on every `refreshTick`. While
   *  the Conversations panel is hidden that is pure cost — nobody is reading
   *  the thread — so it sees the tick frozen at whatever it was when the panel
   *  was last visible, and catches up in ONE refetch on return. */
  const visibleTickRef = useRef(refreshTick);
  if (tab === 'conversations') visibleTickRef.current = refreshTick;
  const boardRefreshTick = visibleTickRef.current;

  const goTab = useCallback((next: ConsoleTab, nextSub?: ManageSub) => {
    setParams((p) => {
      p.set('tab', next);
      if (nextSub) p.set('sub', nextSub);
      return p;
    }, { replace: true });
  }, [setParams]);

  /** "Show me this thread" from anywhere — ONE param update: two consecutive
   *  updaters would each read the committed location and the second would
   *  silently discard the first's edit (the classic console's hard-won note). */
  const openConversation = useCallback((id: string) => {
    setParams((p) => {
      p.set('tab', 'conversations');
      p.set('conversation', id);
      p.delete('review');
      return p;
    }, { replace: true });
  }, [setParams]);

  /** Selection inside the board — the tab is already Conversations. */
  const selectConversation = useCallback((id: string | null) => {
    setParams((p) => {
      if (id) p.set('conversation', id); else p.delete('conversation');
      return p;
    }, { replace: true });
  }, [setParams]);

  const onSubChange = useCallback((next: ManageSub) => goTab('manage', next), [goTab]);

  // ---- the ONE Needs-you derivation ------------------------------------
  const needs = useMemo(
    () => buildNeedsYou(queueEscalations, queueReviews, drafts, suppressed),
    [queueEscalations, queueReviews, drafts, suppressed],
  );
  const needsCount = needsYouCount(needs, activeEsc);
  const needsCapped = needsYouCapped(needs, activeEsc);

  /** Draft-pending lookup for the board's rows (a dot on rows with work waiting). */
  const draftConvoIds = useMemo(
    () => new Set([...drafts, ...suppressed].map((d) => d.conversation_id)),
    [drafts, suppressed],
  );

  const boardCapped = board.length >= BOARD_LIMIT;
  // SegmentedTabs' badge renders a plain number, so a capped count says so in
  // the label instead — the classic console hit the same limitation.
  const tabs = useMemo<SegTab[]>(() => ([
    {
      value: 'needs',
      label: needsCapped ? `Needs you ${needsCount}+` : 'Needs you',
      count: needsCapped ? null : needsCount,
    },
    {
      value: 'conversations',
      label: boardCapped ? `Conversations ${board.length}+` : 'Conversations',
      count: boardCapped ? null : board.length,
    },
    { value: 'manage', label: 'Manage' },
  ]), [needsCapped, needsCount, boardCapped, board.length]);

  return (
    <div>
      <ConsoleKpis
        metrics={metrics}
        needsYouCount={needsCount}
        needsYouCapped={needsCapped}
        onGoConversations={() => goTab('conversations')}
        onGoNeeds={() => goTab('needs')}
        onGoReview={() => goTab('manage', 'review')}
        onGoTuning={() => goTab('manage', 'tuning')}
      />

      <div className="mt-4">
        <SegmentedTabs
          tabs={tabs}
          value={tab}
          onChange={(v) => { if (isConsoleTab(v)) goTab(v); }}
          label="Console sections"
        />
      </div>

      {/* Each panel mounts on first visit and then stays mounted — only
          `hidden` moves after that. Flipping to Conversations to check a thread
          and back must not throw away a half-written determination. */}
      <div className={tab === 'needs' ? '' : 'hidden'}>
        {visited.has('needs') && (
        <NeedsYouTab
          needs={needs}
          escalations={escalations}
          determinations={determinations}
          escStatus={escStatus}
          onEscStatusChange={onEscStatusChange}
          studies={studies}
          loading={loading}
          nowTs={nowTs}
          busyId={busyId}
          reviewParam={reviewParam}
          onOpenConversation={openConversation}
          onApprove={onApprove}
          onReject={onReject}
          onEscalationAction={onEscalationAction}
          onError={onError}
          onRefresh={onRefresh}
        />
        )}
      </div>

      <div className={tab === 'conversations' ? '' : 'hidden'}>
        {!visited.has('conversations') ? null : loading && board.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
          </div>
        ) : (
          <BoardLanes
            board={board}
            closedBoard={closedBoard}
            draftConvoIds={draftConvoIds}
            selectedConvoId={selectedConvoId}
            onSelectConversation={selectConversation}
            nowTs={nowTs}
            busyId={busyId}
            refreshTick={boardRefreshTick}
            studies={studies}
            flagsOnly={flagsOnly}
            setFlagsOnly={setFlagsOnly}
            recentReplyOnly={recentReplyOnly}
            setRecentReplyOnly={setRecentReplyOnly}
            engagedOnly={engagedOnly}
            setEngagedOnly={setEngagedOnly}
            modeFilter={modeFilter}
            setModeFilter={setModeFilter}
            studyFilter={studyFilter}
            setStudyFilter={setStudyFilter}
            onConversationAction={onConversationAction}
            onEscalationAction={onEscalationAction}
            onApprove={onApprove}
            onReject={onReject}
            onError={onError}
          />
        )}
      </div>

      <div className={tab === 'manage' ? '' : 'hidden'}>
        {visited.has('manage') && (
        <ManageTab
          sub={sub}
          onSubChange={onSubChange}
          batches={batches}
          batchesAgentEnabled={batchesAgentEnabled}
          busyId={busyId}
          openBatchId={openBatchId}
          batchDetail={batchDetail}
          loading={loading}
          onToggleBatch={onToggleBatch}
          onCancelBatch={onCancelBatch}
          onOpenConversation={openConversation}
        />
        )}
      </div>
    </div>
  );
}
